import { useEffect, useState } from 'react';
import { CircleMarker, Tooltip } from 'react-leaflet';
import firmsService from '../services/firmsService';
import FireTooltip from './FireTooltip';

/**
 * Renders NASA FIRMS active fire detections as small markers.
 *
 * @param {Object} props
 * @param {Object} props.center - { lat, lng } used as the middle of the bbox.
 * @param {boolean} props.visible - When false, nothing is fetched or rendered.
 */
function FireLayer({ center, visible }) {
  const [fires, setFires] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !center) {
      setFires(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setError(null);
    setFires(null);

    (async () => {
      try {
        const bbox = firmsService.bboxAround(center, 2.5);
        const data = await firmsService.fetchFIRMSFiresGeoJSON(bbox);
        if (!cancelled) setFires(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [visible, center]);

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[FireLayer]', error);
  }

  if (!visible || !fires?.features?.length) return null;

  return (
    <>
      {error && null /* logged above; keep map uncluttered */}
      {fires.features.map((feature) => {
        const { coordinates } = feature.geometry || {};
        if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
        const [lng, lat] = coordinates;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        const p = feature.properties || {};
        return (
          <CircleMarker
            key={`fire-${lat}-${lng}-${p.acquiredAt || ''}`}
            center={[lat, lng]}
            radius={4}
            pathOptions={{
              color: '#b91c1c',
              fillColor: '#ff4500',
              fillOpacity: 0.9,
              weight: 1,
            }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <FireTooltip properties={p} />
            </Tooltip>
          </CircleMarker>
        );
      })}
    </>
  );
}

export default FireLayer;
